import { Clock, ShieldCheck, Sparkles, TrendingUp } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ReactElement } from 'react';

import type { ClaimDemandSignalResponse } from '@/types/professionals-types';

/**
 * Decide which demand-signal copy the claim hero shows (#5369).
 *
 * Mirrors the claim-activation email's conditional + qualitative-fallback:
 * a concrete count only when the backend flagged `show_concrete` AND the
 * count is strictly positive — so a bare `0` is NEVER rendered as a concrete
 * number at the conversion moment. Anything else (null signal, below
 * threshold, or a degraded-to-0 response) falls back to qualitative copy.
 *
 * Exported for unit testing the branch logic without rendering.
 */
export function resolveDemandVariant(
  signal: ClaimDemandSignalResponse | null
):
  | { variant: 'concrete'; count: number; windowDays: number }
  | { variant: 'qualitative' } {
  if (signal && signal.show_concrete && signal.impressions_30d > 0) {
    return {
      variant: 'concrete',
      count: signal.impressions_30d,
      windowDays: signal.window_days,
    };
  }
  return { variant: 'qualitative' };
}

interface ClaimPageHeroProps {
  /** Public per-lawyer demand signal (#5369). `null` until loaded or on any
      failure — the hero then shows qualitative copy. */
  demandSignal?: ClaimDemandSignalResponse | null;
}

/**
 * Centered hero for the claim page — gradient icon tile + title + subtitle +
 * three trust pills (SAK verified · Free · ~2 min) + a conditional per-lawyer
 * demand signal (#5369) that reinforces the conversion moment.
 *
 * Trust pills are hardcoded copy (no translation key yet) so the design can
 * stabilize; when finalized they'll move into Lawyers.claim.trust.* and the
 * strings will come from useTranslations like everything else here.
 */
export function ClaimPageHero({
  demandSignal = null,
}: ClaimPageHeroProps): ReactElement {
  const t = useTranslations('Lawyers.claim');
  const demand = resolveDemandVariant(demandSignal);

  return (
    <div className='text-center mb-6'>
      <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-tertiary/25 to-status-info/25 ring-1 ring-brand-tertiary/40 shadow-sm'>
        <ShieldCheck className='h-8 w-8 text-brand-tertiary' />
      </div>

      <h1 className='text-2xl font-semibold tracking-tight text-content-primary mb-2'>
        {t('title')}
      </h1>
      <p className='text-sm text-content-secondary max-w-sm mx-auto'>
        {t('subtitle')}
      </p>

      <div className='mt-5 flex flex-wrap items-center justify-center gap-2'>
        <TrustPill icon={ShieldCheck} label='SAK overené' tone='info' />
        <TrustPill icon={Sparkles} label='Zdarma' tone='success' />
        <TrustPill icon={Clock} label='~2 minúty' tone='brand' />
      </div>

      <div className='mt-5 mx-auto max-w-sm flex items-start gap-2 rounded-xl border border-brand-tertiary/20 bg-brand-tertiary/5 px-3.5 py-3 text-left'>
        <TrendingUp className='mt-0.5 h-4 w-4 shrink-0 text-brand-tertiary' />
        <p className='text-sm text-content-primary leading-relaxed'>
          {demand.variant === 'concrete'
            ? t('demandSignalConcrete', {
                count: demand.count,
                windowDays: demand.windowDays,
              })
            : t('demandSignalQualitative')}
        </p>
      </div>
    </div>
  );
}

interface TrustPillProps {
  icon: typeof ShieldCheck;
  label: string;
  tone: 'info' | 'success' | 'brand';
}

function TrustPill({ icon: Icon, label, tone }: TrustPillProps): ReactElement {
  const toneClass = {
    info: 'bg-status-info/10 text-status-info border-status-info/20',
    success:
      'bg-status-success/10 text-status-success border-status-success/20',
    brand: 'bg-brand-tertiary/10 text-brand-tertiary border-brand-tertiary/20',
  }[tone];

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${toneClass}`}
    >
      <Icon className='h-3 w-3' />
      {label}
    </span>
  );
}
